// ./src/contexts/ServiceIntervalAccountsContext.tsx
//
// Context wrapper around the useServiceIntervalAccounts hook.
//
// Purpose:
// - Allows sibling components (form, toolbar, page content)
//   to share the same service interval accounts state without prop-drilling.
// - Each page creates its own <ServiceIntervalAccountsProvider> with its own config,
//   so different pages don't interfere with each other.
// - The underlying useServiceIntervalAccounts hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In a page component:
//   <ServiceIntervalAccountsProvider autoLoad={false} carId={selectedCarId}>
//     <ServiceIntervalsPageContent />
//   </ServiceIntervalAccountsProvider>
//
//   // In any child component:
//   const { serviceIntervalAccounts, loading, set, ... } = useServiceIntervalAccountsContext();
//
//   // In a component that may or may not be inside a provider:
//   const context = useOptionalServiceIntervalAccountsContext();
//   const saveFn = context?.set ?? props.onSave;

import React, { createContext, useContext } from "react";
import {
  useServiceIntervalAccounts,
  UseServiceIntervalAccountsOptions,
  UseServiceIntervalAccountsReturn,
} from "@/hooks/useServiceIntervalAccounts";

// ============================================
// Context
// ============================================

const ServiceIntervalAccountsContext =
  createContext<UseServiceIntervalAccountsReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface ServiceIntervalAccountsProviderProps
  extends UseServiceIntervalAccountsOptions {
  children: React.ReactNode;
}

/**
 * Wraps useServiceIntervalAccounts into a context provider.
 *
 * Each instance creates its own independent useServiceIntervalAccounts store
 * with its own filters, loading state, and error state.
 *
 * @example
 * <ServiceIntervalAccountsProvider autoLoad={false} carId={selectedCarId}>
 *   <MyPageContent />
 * </ServiceIntervalAccountsProvider>
 */
export const ServiceIntervalAccountsProvider: React.FC<
  ServiceIntervalAccountsProviderProps
> = ({ children, ...options }) => {
  const serviceIntervalAccounts = useServiceIntervalAccounts(options);

  return (
    <ServiceIntervalAccountsContext.Provider value={serviceIntervalAccounts}>
      {children}
    </ServiceIntervalAccountsContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the ServiceIntervalAccountsContext. Throws if used outside a provider.
 *
 * Use this in components that are always rendered inside a provider
 * (e.g. ServiceIntervalsForm).
 */
export const useServiceIntervalAccountsContext =
  (): UseServiceIntervalAccountsReturn => {
    const context = useContext(ServiceIntervalAccountsContext);

    if (!context) {
      throw new Error(
        "useServiceIntervalAccountsContext must be used within a <ServiceIntervalAccountsProvider>. " +
        "Wrap your component tree with <ServiceIntervalAccountsProvider> or use useOptionalServiceIntervalAccountsContext() instead."
      );
    }

    return context;
  };

/**
 * Access the ServiceIntervalAccountsContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider.
 *
 * @example
 * const context = useOptionalServiceIntervalAccountsContext();
 * const saveFn = context?.set ?? props.onSave;
 */
export const useOptionalServiceIntervalAccountsContext =
  (): UseServiceIntervalAccountsReturn | null => {
    return useContext(ServiceIntervalAccountsContext);
  };